import { useState, useEffect, useMemo } from "react";
import { Modal, Form, Spin, Upload, Button, notification, Empty } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useCreate, useTranslate } from "@refinedev/core";
import type { UploadFile, UploadProps } from "antd/es/upload";
import { useFormMetadata } from "@/hooks/useFormMetadata";
import { DynamicFormField } from "@/components/DynamicForm/DynamicFormField";
import { DynamicFormSection } from "@/components/DynamicForm/DynamicFormSection";
import { FormItemTypeProvider } from "@/components/DynamicForm/FormItemTypeContext";
import { transformDatesToAPIValues } from "@/utils/dateUtils";
import { ENV } from "@/config/env";

interface CreateRelatedModalProps {
  /** The related ItemType name to create */
  itemTypeName: string;
  /** Whether the modal is visible */
  open: boolean;
  /** Callback when modal is closed */
  onCancel: () => void;
  /** Callback when item is created successfully */
  onCreated: (itemId: string, itemData: Record<string, unknown>) => void;
  /** Modal title */
  title?: string;
}

/**
 * Modal for creating a new related item inline.
 * Dynamically generates form fields based on item type metadata.
 * For File type, shows file upload.
 * For other types, shows form fields for required properties.
 */
export const CreateRelatedModal: React.FC<CreateRelatedModalProps> = ({
  itemTypeName,
  open,
  onCancel,
  onCreated,
  title,
}) => {
  const translate = useTranslate();
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const { mutate: createItem, isLoading: isCreating } = useCreate();
  const [isUploading, setIsUploading] = useState(false);

  // Fetch metadata for the item type
  // useFormMetadata returns fields already filtered by:
  // - System fields excluded
  // - Hidden fields excluded (unless explicitly in form layout)
  // - Read-only fields excluded in create mode
  const {
    fields,
    sections,
    hasResponsiveForm,
    defaultSiteId,
    isLoading: isMetadataLoading,
  } = useFormMetadata({
    itemTypeName,
    mode: "create",
    enabled: open && itemTypeName.toLowerCase() !== "file",
  });

  // Check if this is a File type
  const isFileType = itemTypeName.toLowerCase() === "file";

  // Filter and sort fields based on whether Aras Responsive Form is configured
  // Hybrid approach:
  // 1. If Responsive Form exists: Show ALL fields from form layout (admin-controlled)
  // 2. If no Responsive Form: Show all non-hidden fields (automatic discovery)
  // This eliminates hardcoded priority lists and makes the system fully dynamic
  const formFields = useMemo(() => {
    if (!fields || fields.length === 0) return [];

    // Additional system/internal fields to exclude from quick create form
    // These are fields that should never appear in a create modal
    // (useFormMetadata already filters most system fields, but these are extra safety)
    const excludeFieldNames = new Set([
      "itemNumber",
      "item_number",
      "css", // Internal styling field
    ]);

    // Filter fields
    const filteredFields = fields.filter((field) => {
      // Exclude specific system fields not caught by useFormMetadata
      if (excludeFieldNames.has(field.name)) return false;
      if (excludeFieldNames.has(field.mappedName)) return false;

      // If Responsive Form exists, trust the form layout completely
      // All fields from useFormMetadata are already filtered by form layout
      if (hasResponsiveForm) {
        return true;
      }

      // No Responsive Form: show all non-hidden fields
      // useFormMetadata already filtered hidden fields, so just return true
      return true;
    });

    // Sort: required fields first, then by Aras sortOrder
    return filteredFields.sort((a, b) => {
      if (a.isRequired && !b.isRequired) return -1;
      if (!a.isRequired && b.isRequired) return 1;
      return a.sortOrder - b.sortOrder;
    });
  }, [fields, hasResponsiveForm]);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      form.resetFields();
      setFileList([]);
    }
  }, [open, form]);

  // Set default site_id on create mode when site scoping is enabled
  useEffect(() => {
    if (open && defaultSiteId && !isMetadataLoading) {
      const currentSiteId = form.getFieldValue("siteId");
      if (!currentSiteId) {
        form.setFieldsValue({ siteId: defaultSiteId });
      }
    }
  }, [open, defaultSiteId, isMetadataLoading, form]);

  // Handle form submission
  const handleSubmit = async () => {
    try {
      const rawValues = await form.validateFields();
      // Transform dayjs dates to API-compatible strings (same as DynamicForm)
      const values = transformDatesToAPIValues(rawValues) as Record<string, unknown>;

      if (isFileType && fileList.length === 0) {
        notification.error({
          message: translate("errors.validationError", "Validation Error"),
          description: translate("errors.selectFile", "Please select a file"),
        });
        return;
      }

      // For File type, handle file upload
      if (isFileType && fileList.length > 0) {
        const file = fileList[0] as unknown as File;
        const formData = new FormData();
        formData.append("file", file);

        setIsUploading(true);

        try {
          const accessToken = localStorage.getItem("access_token");

          const response = await fetch(`${ENV.API_URL}/files/upload`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            body: formData,
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(
              errorData.error || `Upload failed: ${response.statusText}`,
            );
          }

          const result = await response.json();

          notification.success({
            message: translate("messages.success", "Success"),
            description: translate(
              "messages.fileUploaded",
              "File uploaded successfully",
            ),
          });

          // Return the file ID and data
          onCreated(result.id, {
            id: result.id,
            fileName: result.fileName,
            fileSize: result.fileSize,
            contentType: result.contentType,
          });
          onCancel();
        } catch (error) {
          notification.error({
            message: translate("errors.uploadFailed", "Upload Failed"),
            description:
              error instanceof Error
                ? error.message
                : translate("errors.unknownError", "Unknown error occurred"),
          });
        } finally {
          setIsUploading(false);
        }
        return;
      }

      // For other types, create the item
      createItem(
        {
          resource: itemTypeName,
          values: values,
        },
        {
          onSuccess: (data) => {
            const createdItem = data?.data;
            if (createdItem?.id) {
              notification.success({
                message: translate("messages.success", "Success"),
                description: translate(
                  "messages.itemCreated",
                  "Item created successfully",
                ),
              });
              onCreated(String(createdItem.id), createdItem);
              onCancel();
            }
          },
          onError: (error) => {
            notification.error({
              message: translate(
                "errors.createFailed",
                "Failed to create item",
              ),
              description: error.message,
            });
          },
        },
      );
    } catch {
      // Form validation error - do nothing, Ant Design will show the errors
    }
  };

  // File upload props
  const uploadProps: UploadProps = {
    beforeUpload: (file) => {
      setFileList([file]);
      return false; // Prevent auto upload
    },
    onRemove: () => {
      setFileList([]);
    },
    fileList,
  };

  // Calculate visible groupbox indices for auto-collapse logic (same as DynamicForm)
  const visibleGroupboxIndices = useMemo(() => {
    const indices: number[] = [];
    sections.forEach((section, index) => {
      const hasTitle = section.title || section.titleKey;
      const isBordered = section.bordered === true;
      if (hasTitle || isBordered) {
        indices.push(index);
      }
    });
    return indices;
  }, [sections]);

  // Render form content based on item type
  const renderFormContent = () => {
    // File type gets special upload UI
    if (isFileType) {
      return (
        <Form.Item
          name="file"
          label={translate("fields.file", "File")}
          rules={[
            {
              required: true,
              message: translate(
                "validation.required",
                "This field is required",
              ),
            },
          ]}
        >
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />}>
              {translate("buttons.selectFile", "Select File")}
            </Button>
          </Upload>
        </Form.Item>
      );
    }

    // Loading state
    if (isMetadataLoading) {
      return (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin size="large" />
        </div>
      );
    }

    // No fields found
    if (formFields.length === 0) {
      return (
        <Empty
          description={translate(
            "messages.noFormFields",
            "No form fields available for this item type",
          )}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      );
    }

    // If Responsive Form is configured, render sections (same layout as full page)
    if (hasResponsiveForm && sections.length > 0) {
      return sections.map((section, index) => {
        const groupboxPosition = visibleGroupboxIndices.indexOf(index);
        const isVisibleGroupbox = groupboxPosition !== -1;

        const sectionFieldNames = new Set(section.fields);
        const hasRequiredFields = formFields.some(
          (f) => sectionFieldNames.has(f.mappedName) && f.isRequired,
        );

        const shouldCollapse =
          isVisibleGroupbox &&
          groupboxPosition > 0 &&
          visibleGroupboxIndices.length > 1 &&
          !hasRequiredFields;

        return (
          <DynamicFormSection
            key={section.key}
            config={section}
            fields={formFields}
            form={form}
            readOnly={false}
            defaultCollapsed={shouldCollapse}
          />
        );
      });
    }

    // Non-responsive form: render fields as flat list
    return formFields.map((field) => (
      <DynamicFormField
        key={field.id || field.name}
        field={field}
        form={form}
        disabled={false}
        showAddNew={false}
      />
    ));
  };

  const modalTitle =
    title || `${translate("buttons.createNew", "Create New")} ${itemTypeName}`;

  const isLoading = isCreating || isMetadataLoading || isUploading;

  // Use wider modal when responsive form has multi-column layout
  const modalWidth = hasResponsiveForm ? 900 : 600;

  return (
    <Modal
      title={modalTitle}
      open={open}
      onOk={handleSubmit}
      onCancel={onCancel}
      confirmLoading={isCreating || isUploading}
      okText={translate("buttons.create", "Create")}
      cancelText={translate("buttons.cancel", "Cancel")}
      destroyOnClose
      width={modalWidth}
    >
      {isLoading && !isFileType ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin size="large" />
        </div>
      ) : (
        <FormItemTypeProvider value={itemTypeName}>
          <Form form={form} layout="vertical">
            {renderFormContent()}
          </Form>
        </FormItemTypeProvider>
      )}
    </Modal>
  );
};

export default CreateRelatedModal;
